import React, { useState } from 'react';
import { Alert, Box, Button, Link, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { EmailTrackingSetup } from '../../types';
import { formatDateTime } from '../../utils/format';
import CopyField from './CopyField';

interface Props {
  setup: EmailTrackingSetup;
  /** Re-read the setup state (a confirmation or a first copy may have arrived). */
  onCheck: () => void;
  checking: boolean;
}

const Step: React.FC<{ number: number; title: string; children: React.ReactNode }> = ({ number, title, children }) => (
  <Box component="li" sx={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr)', columnGap: 1.5 }}>
    <Box
      aria-hidden
      sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14 }}
    >
      {number}
    </Box>
    <Stack spacing={1}>
      <Typography variant="subtitle1" component="h4" sx={{ fontWeight: 600, lineHeight: '28px' }}>
        {title}
      </Typography>
      {children}
    </Stack>
  </Box>
);

/** The one-time setup of the forwarding rule in the user's own mailbox. */
const CopySetup: React.FC<Props> = ({ setup, onCheck, checking }) => {
  const [provider, setProvider] = useState<'gmail' | 'outlook'>('gmail');
  const working = setup.copies_received > 0;
  const confirmation = setup.confirmation;
  return (
    <Paper variant="outlined" component="section" aria-labelledby="copy-setup-title" sx={{ p: 2 }}>
      <Typography id="copy-setup-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
        Set up the forwarding rule
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Done once, in your mailbox's own settings. JobFlo never signs in to your mailbox.
      </Typography>
      {working ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          It works: {setup.copies_received.toLocaleString('en-US')} {setup.copies_received === 1 ? 'copy has' : 'copies have'} arrived
          {setup.last_copy_at ? `, the latest ${formatDateTime(setup.last_copy_at)}` : ''}.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          No copy has arrived yet. After the steps below, the next email from a hiring system shows up here.
        </Alert>
      )}
      <Tabs value={provider} onChange={(_, value) => setProvider(value)} aria-label="Mailbox provider" sx={{ mb: 2, minHeight: 40 }}>
        <Tab value="gmail" label="Gmail" sx={{ minHeight: 40 }} />
        <Tab value="outlook" label="Outlook" sx={{ minHeight: 40 }} />
      </Tabs>

      {provider === 'gmail' ? (
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2.5 }}>
          <Step number={1} title="Add your JobFlo address as a forwarding address">
            <Typography variant="body2">
              In Gmail on a computer open Settings, "See all settings", then "Forwarding and POP/IMAP", and choose "Add a
              forwarding address". Enter this address:
            </Typography>
            <CopyField label="Your JobFlo address" value={setup.address} />
          </Step>
          <Step number={2} title="Confirm it">
            <Typography variant="body2">
              Gmail sends a confirmation to that address. It arrives here, not in your inbox.
            </Typography>
            {confirmation ? (
              <Alert severity="success" icon={false}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Gmail's confirmation{confirmation.mailbox ? ` for ${confirmation.mailbox}` : ''} arrived {formatDateTime(confirmation.received_at)}.
                  {confirmation.code && ` Confirmation code: ${confirmation.code}.`}
                </Typography>
                {/^https:\/\/(mail-settings|mail)\.google\.com\//.test(confirmation.confirm_url) && (
                  <Button variant="contained" size="small" component={Link} href={confirmation.confirm_url} target="_blank" rel="noopener noreferrer">
                    Open Gmail's confirmation page
                  </Button>
                )}
              </Alert>
            ) : (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Nothing has arrived yet. It usually takes under a minute.
                </Typography>
                <Button size="small" variant="outlined" onClick={onCheck} disabled={checking}>
                  {checking ? 'Checking...' : 'Check again'}
                </Button>
              </Stack>
            )}
          </Step>
          <Step number={3} title="Create the filter">
            <Typography variant="body2">
              In Gmail's settings open "Filters and blocked addresses" and choose "Create a new filter". Paste this into
              the "From" box, choose "Create filter", tick "Forward it to" and pick your JobFlo address.
            </Typography>
            <CopyField label="Filter: From" value={setup.gmail_filter_from} multiline />
            <Typography variant="body2" color="text.secondary">
              These are the mail systems employers use. Mail from anyone else stays private; add a company's own domain to
              the list if you want its recruiters' mail tracked too.
            </Typography>
          </Step>
        </Box>
      ) : (
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 2.5 }}>
          <Step number={1} title="Create a rule">
            <Typography variant="body2">
              In Outlook on the web open Settings, then Mail, then Rules, and choose "Add new rule".
            </Typography>
          </Step>
          <Step number={2} title="Match mail from hiring systems">
            <Typography variant="body2">
              As the condition choose "Sender address includes" and add these, one at a time:
            </Typography>
            <CopyField label="Sender address includes" value={setup.sender_domains.join(', ')} multiline />
          </Step>
          <Step number={3} title="Redirect a copy to JobFlo">
            <Typography variant="body2">
              As the action choose "Redirect to" (it keeps the original sender, which "Forward to" does not) and enter your
              JobFlo address. Leave "Stop processing more rules" unticked so the mail also stays in your inbox.
            </Typography>
            <CopyField label="Your JobFlo address" value={setup.address} />
          </Step>
        </Box>
      )}
      {!working && (
        <Button size="small" onClick={onCheck} disabled={checking} sx={{ mt: 2 }}>
          {checking ? 'Checking...' : 'Check whether a copy has arrived'}
        </Button>
      )}
    </Paper>
  );
};

export default CopySetup;
